import "./button.scss";
import classNames from "classnames";
import { LoadingSpinner } from "./loading_spinner";

export type ButtonShape = "rectangle" | "rounded" | "pill";
type ButtonType = "submit" | "button" | "reset";

interface ButtonProps {
  children: string;
  shape?: ButtonShape | null;
  type?: ButtonType;
  secondary?: boolean;
  tertiary?: boolean;
  ariaLabel?: string;
  link?: false;
  href?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  testId?: string;
  target?: string;
}

interface LinkButtonProps {
  children: string;
  shape?: ButtonShape | null;
  type?: ButtonType;
  secondary?: boolean;
  tertiary?: boolean;
  ariaLabel?: string;
  link: true;
  href?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  testId?: string;
  target?: string;
}

const isLinkButton = (button: ButtonProps | LinkButtonProps): button is LinkButtonProps => {
  return (button as LinkButtonProps).link;
};

export const Button = (props: ButtonProps | LinkButtonProps) => {
  const {
    children,
    shape = "pill",
    type = "button",
    secondary = false,
    tertiary = false,
    ariaLabel,
    href,
    loading,
    disabled,
    testId,
    target,
  } = props;

  const className = classNames({
    btn: !tertiary,
    [`btn--${shape}`]: !tertiary,
    "btn--secondary": !tertiary && secondary,
    "btn--tertiary": tertiary,
    btn__disabled: disabled,
  });

  if (isLinkButton(props)) {
    return (
      <a
        href={href}
        target={target || "_top"}
        className={className}
        aria-label={ariaLabel}
        onClick={props.onClick} // typescript gets confused if using destructuring
      >
        {children}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={className}
      aria-label={ariaLabel}
      onClick={props.onClick}
      disabled={disabled}
      aria-disabled={disabled}
      data-testid={testId}
    >
      {loading && <LoadingSpinner size="small" />}
      {!loading && children}
    </button>
  );
};
